<div class="container">
  <h4 class="mb-3">Special project — Excel audio generation</h4>
  <p class="text-muted small mb-4">
    Create or select an audio-gen project, upload <code>AudioGenFormat.xlsx</code>
    (agents in columns A–F, up to 25 lines in H–K; max 25 lines),
    generate Narakeet MP3 files as <code>{{ '{' }}projectId{{ '}' }}_{{ '{' }}lineId{{ '}' }}.mp3</code>, then download a ZIP.
    Empty text, unknown agent id, and timeouts are skipped.
  </p>

  <div class="card mb-3">
    <div class="card-header">1. Project</div>
    <div class="card-body">
      <div class="row g-2 align-items-end mb-3">
        <div class="col-md-6">
          <label class="form-label">Existing audio-gen project</label>
          <select
            class="form-select"
            [(ngModel)]="selectedProjectCode"
            (ngModelChange)="onProjectChange()"
            [disabled]="generating || uploading">
            <option value="">— Select —</option>
            <option *ngFor="let p of projects" [value]="p.ProjectShortCode">
              {{ p.ProjectShortCode }} — {{ p.ProjectName }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <button type="button" class="btn btn-outline-secondary" (click)="reloadProjects()" [disabled]="generating">
            Refresh list
          </button>
        </div>
      </div>

      <div class="border rounded p-3 bg-light">
        <div class="fw-semibold mb-2">Or create new</div>
        <div class="row g-2 align-items-end">
          <div class="col-md-3">
            <label class="form-label">Short code</label>
            <input class="form-control" [(ngModel)]="newProject.ProjectShortCode" maxlength="45" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Name</label>
            <input class="form-control" [(ngModel)]="newProject.ProjectName" maxlength="200" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Customer</label>
            <select class="form-select" [(ngModel)]="newProject.CustomerShortCode">
              <option value="">—</option>
            <option *ngFor="let c of customers" [value]="customerCode(c)">
              {{ customerCode(c) }}{{ customerName(c) ? ' — ' + customerName(c) : '' }}
            </option>
            </select>
          </div>
          <div class="col-md-2">
            <button
              type="button"
              class="btn btn-primary w-100"
              (click)="createProject()"
              [disabled]="creating || generating">
              {{ creating ? 'Creating…' : 'Create' }}
            </button>
          </div>
        </div>
        <div *ngIf="createStatus" class="small text-primary mt-2">{{ createStatus }}</div>
        <div *ngIf="createError" class="small text-danger mt-2" style="white-space: pre-line">{{ createError }}</div>
      </div>
    </div>
  </div>

  <div class="card mb-3">
    <div class="card-header">2. Upload Excel &amp; generate</div>
    <div class="card-body">
      <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
        <label class="btn btn-outline-primary mb-0" [class.disabled]="!selectedProjectCode || uploading || generating">
          {{ uploading ? 'Uploading…' : 'Upload Excel' }}
          <input
            type="file"
            accept=".xlsx,.xls"
            hidden
            (change)="onFileSelected($event)"
            [disabled]="!selectedProjectCode || uploading || generating" />
        </label>
        <span class="text-muted small" *ngIf="selectedFileName">{{ selectedFileName }}</span>
        <button
          type="button"
          class="btn btn-success"
          (click)="generate()"
          [disabled]="!selectedProjectCode || !lines.length || generating || uploading">
          {{ generating ? 'Generating…' : 'Generate audio' }}
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          (click)="downloadZip()"
          [disabled]="!selectedProjectCode || downloading || generating || doneCount() === 0">
          {{ downloading ? 'Preparing ZIP…' : 'Download audio ZIP' }}
        </button>
      </div>

      <div *ngIf="generating || progressMessage" class="mb-3">
        <div class="d-flex justify-content-between small mb-1">
          <span>{{ progressMessage }}</span>
          <span>{{ progressPercent }}%</span>
        </div>
        <div class="progress" style="height: 0.75rem;">
          <div
            class="progress-bar"
            role="progressbar"
            [style.width.%]="progressPercent"
            [attr.aria-valuenow]="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"></div>
        </div>
      </div>
      <div class="small text-success" *ngIf="lastSummary && !generating">{{ lastSummary }}</div>
    </div>
  </div>

  <div class="card" *ngIf="lines.length">
    <div class="card-header">Lines ({{ lines.length }})</div>
    <div class="card-body p-0">
      <table class="table table-sm table-striped mb-0">
        <thead>
          <tr>
            <th>#</th>
            <th>Line id (H)</th>
            <th>Agent id (I)</th>
            <th>Voice</th>
            <th>Text preview</th>
            <th>Status</th>
            <th>File</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let line of lines">
            <td>{{ line.lineSrNo }}</td>
            <td>{{ line.lineId }}</td>
            <td>{{ line.agentId || '—' }}</td>
            <td>{{ line.agentName || '—' }}</td>
            <td class="small text-muted">
              {{ line.textPreview || (line.scriptText || '').slice(0, 80) || '—' }}
            </td>
            <td>
              <span
                class="badge"
                [class.text-bg-secondary]="!line.status || line.status === 'PENDING'"
                [class.text-bg-success]="line.status === 'DONE'"
                [class.text-bg-warning]="line.status === 'SKIPPED'"
                [class.text-bg-danger]="line.status === 'ERROR'"
                [class.text-bg-info]="line.status === 'RUNNING'">
                {{ line.status || 'PENDING' }}
              </span>
              <div class="small text-muted" *ngIf="line.statusMessage">{{ line.statusMessage }}</div>
            </td>
            <td class="small">{{ line.audioFileName || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>
